import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { riskApi } from '../../utils/riskApi';
import StagingReview from './StagingReview';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';

const CONF_STYLE = {
  exact: { background: '#DDF3E4', color: '#17603A', label: 'Exact' },
  high: { background: '#E3ECFB', color: '#1F4E9E', label: 'High' },
  suggested: { background: '#FFF1D6', color: '#7A4B00', label: 'Suggested' },
  chosen: { background: '#EAE5F5', color: '#3D2070', label: 'Chosen' },
  unmapped: { background: '#F3F0F8', color: '#6B6580', label: 'Unmapped' },
};
const HOW_STYLE = {
  exact: ['Exact', '#DDF3E4', '#17603A'], number: ['By code', '#DDF3E4', '#17603A'],
  word: ['By word', '#E3ECFB', '#1F4E9E'], similar: ['Similar', '#E3ECFB', '#1F4E9E'],
  chosen: ['Chosen', '#EAE5F5', '#3D2070'], band: ['Banding — confirm', '#FFF1D6', '#7A4B00'],
  ai: ['AI — check', '#FFF1D6', '#7A4B00'], empty: ['Left empty', '#F3F0F8', '#6B6580'],
  none: ['Needs you', '#FBE0D5', '#8A2A0B'],
};

/**
 * Import Excel (mockup screens 4 and 5). /risk/:setupId/import uploads; /risk/import/:batchId
 * is the wizard: 2 Structure · 3 Map fields & values · 4 Review records → Create.
 *
 * Every automatic choice is shown with HOW it was made, and can be changed. A numeric scale
 * that does not fit (1–10 into 1–5) is banded only after the person confirms it.
 */
export function ImportUpload() {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const from = useLocation().state?.from || '';
  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    riskApi.panelConfig(setupId).then(setPanel).catch((e) => setError(e.message));
    riskApi.imports(setupId).then((d) => setHistory(d.imports || [])).catch(() => {});
  }, [setupId]);

  const [prog, setProg] = useState(null);           // {step, steps, pct, since}

  // Only an import that wrote nothing to Orcanos can go; one that did is the record of that write.
  const remove = async (h) => {
    if (!window.confirm(`Delete the import "${h.file_name || 'Import'}"? Its file and mapping are removed.`)) return;
    try {
      await riskApi.deleteImport(h.id);
      setHistory((xs) => xs.filter((x) => x.id !== h.id));
    } catch (e) {
      setError(e.message);
    }
  };

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    setError('');
    setProg({ step: 0, steps: UPLOAD_STEPS, pct: 0, since: Date.now() });
    let st;
    try {
      st = await riskApi.upload(setupId, file, (f) => setProg((p) => p && { ...p, pct: f }));
    } catch (e) {
      setError(e.message);
      setBusy(false);
      setProg(null);
      return;
    }
    const bid = st.batch.id;
    if (st.ai_available) {
      // Two real requests, so the bar moves on real steps. A failure still opens the import:
      // the rule-based guess is there, and the page says why AI did not read it.
      try {
        setProg((p) => ({ ...p, step: 1, pct: 0, since: Date.now() }));
        await riskApi.analyze(bid, 'structure');
        setProg((p) => ({ ...p, step: 2, pct: 0, since: Date.now() }));
        await riskApi.analyze(bid, 'values');
      } catch (_) { /* shown on the next page as ai_error */ }
    }
    setProg((p) => ({ ...p, step: UPLOAD_STEPS.length, pct: 1 }));
    navigate(`/risk/import/${bid}`, { state: from ? { from } : undefined });
  };

  return (
    <div className="drv-page rsk-page">
      <Crumbs panel={panel} last="Import" back={from} />
      <Steps at={1} />
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Import risks from Excel</h1>
          <p className="drv-sub">Into <strong>{panel?.name || '…'}</strong>, in the Orcanos project shown above. Nothing is written to Orcanos until you review every record and press Create.</p>
        </div>
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}
      {prog ? <UploadProgress prog={prog} /> : (
      <label className={`rsk-drop${busy ? ' rsk-drop--busy' : ''}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}>
        <input type="file" accept=".xlsx,.csv" onChange={(e) => pick(e.target.files?.[0])} disabled={busy} />
        <strong>{busy ? 'Reading the file — AI is working out its layout and mapping (about 20 seconds)…' : 'Drop an .xlsx or .csv file here, or click to choose'}</strong>
        <span>Up to 10 MB and 250 risks. AI reads the first rows of each sheet and proposes the sheet, header and mapping — you confirm every choice next.</span>
      </label>
      )}
      {history.length > 0 && (
        <section className="rsk-card rsk-pad">
          <div className="rsk-lbl">Earlier imports</div>
          {history.map((h) => (
            <div className="rsk-hist" key={h.id}>
              <div><strong>{h.file_name || (h.source === 'studio' ? 'Risk Studio' : 'Import')}</strong>
                <span className="rsk-fine"> · {h.created_by_name} · {String(h.created_at || '').slice(0, 16)} UTC</span></div>
              <div className="rsk-actions">
                <span className="rsk-fine">{h.status}{h.result?.created ? ` · ${h.result.created} created` : ''}</span>
                <Link className="drv-btn" to={`/risk/import/${h.id}`} state={from ? { from } : undefined}>Open</Link>
                {!h.written && h.status !== 'creating' && (
                  <button type="button" className="drv-btn" onClick={() => remove(h)}
                    title="Delete this import — nothing from it was written to Orcanos">Delete</button>
                )}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

// The step and the mapping picks not yet saved, per import — so signing out (or timing out) and
// back in returns to the same screen with the same choices. A draft is restored only onto the
// mapping it was made against: if the saved mapping changed since, the server's wins.
const draftKey = (bid) => `tm.risk.import.${bid}`;
const readDraft = (bid) => {
  try { return JSON.parse(localStorage.getItem(draftKey(bid)) || 'null'); } catch (_) { return null; }
};

export default function ImportPage() {
  const { batchId } = useParams();
  const location = useLocation();
  const [st, setSt] = useState(null);
  const [panel, setPanel] = useState(null);
  const [step, setStep] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [fields, setFields] = useState({});          // col → target
  const [nameFrom, setNameFrom] = useState([]);
  // ONLY the person's explicit picks. A proposal (a match, a banding) is shown but never sent
  // back as a choice — otherwise a banding would be applied without its confirmation.
  const [values, setValues] = useState({});          // target → {value: option}
  const [bands, setBands] = useState([]);            // confirmed band targets
  const [defaults, setDefaults] = useState({});      // target → the same value for every record
  const [saveTpl, setSaveTpl] = useState(true);
  const [vtab, setVtab] = useState('');
  const base = useRef('');                           // the saved mapping the picks were made on

  const apply = useCallback((s) => {
    base.current = JSON.stringify(s.mapping || []);
    setSt(s);
    setFields(Object.fromEntries((s.mapping || []).map((m) => [m.col, m.target])));
    setNameFrom(s.name_from || []);
    setDefaults(s.defaults || {});
    const vals = {};
    (s.values || []).forEach((v) => {
      vals[v.target] = Object.fromEntries(v.values.filter((x) => x.how === 'chosen').map((x) => [x.value, x.option || '']));
    });
    setValues(vals);
    setBands((s.values || []).filter((v) => v.band_confirmed).map((v) => v.target));
    setVtab((cur) => cur || s.values?.[0]?.target || '');
  }, []);

  useEffect(() => {
    let live = true;
    riskApi.importState(batchId).then((s) => {
      if (!live) return;
      apply(s);
      const d = readDraft(batchId);
      if (d && d.base === base.current && d.picks) {
        setFields(d.picks.fields || {});
        setNameFrom(d.picks.nameFrom || []);
        setValues(d.picks.values || {});
        setBands(d.picks.bands || []);
        setDefaults(d.picks.defaults || {});
        if (typeof d.picks.saveTpl === 'boolean') setSaveTpl(d.picks.saveTpl);
      }
      const staged = s.batch.source !== 'import' || (s.counts?.all || 0) > 0;
      setStep((cur) => cur || (d && (d.step === 3 || (d.step === 4 && staged)) ? d.step : (staged ? 4 : 3)));
      riskApi.panelConfig(s.batch.setup_id).then((p) => { if (live) setPanel(p); }).catch(() => {});
    }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [batchId, apply]);

  useEffect(() => {
    if (!st || !step) return;
    try {
      localStorage.setItem(draftKey(batchId), JSON.stringify({
        step, base: base.current, picks: { fields, nameFrom, values, bands, defaults, saveTpl },
      }));
    } catch (_) { /* private mode / full — the server still has the saved mapping */ }
  }, [batchId, st, step, fields, nameFrom, values, bands, defaults, saveTpl]);

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try { return await fn(); } catch (e) { setError(e.message); return null; } finally { setBusy(''); }
  };

  const setStructure = (patch) => run('structure', async () => {
    const s = st.structure;
    const res = await riskApi.setStructure(batchId, { ...s, ...patch });
    apply(res);
  });

  const mappingBody = () => ({
    fields: Object.entries(fields).map(([col, target]) => ({ col: Number(col), target })),
    name_from: nameFrom,
    values: Object.fromEntries(Object.entries(values).map(([t, vm]) => [t, Object.fromEntries(
      Object.entries(vm).filter(([, o]) => o !== undefined))])),
    confirmed_bands: bands,
    defaults,
  });

  const saveMapping = () => run('mapping', async () => apply(await riskApi.setMapping(batchId, mappingBody())));

  // A scoring-method mismatch is resolved by the person: keep those columns as description text.
  const keepAsText = (cols) => run('mapping', async () => {
    const next = { ...fields };
    cols.forEach((c) => { next[c] = '__desc__'; });
    setFields(next);
    apply(await riskApi.setMapping(batchId, { ...mappingBody(), fields: Object.entries(next).map(([col, target]) => ({ col: Number(col), target })) }));
  });

  const toReview = () => run('stage', async () => {
    await riskApi.setMapping(batchId, { ...mappingBody(), save_template: saveTpl });
    await riskApi.stage(batchId);
    setStep(4);
  });

  // A fresh AI proposal REPLACES the mapping (and the picks made on it) — so it asks first.
  const askAi = () => {
    if (st.ai && !window.confirm('Read the file again with AI? Your changes to the mapping on this page are replaced.')) return;
    run('ai', async () => {
      const res = await riskApi.analyze(batchId);
      setFields({});
      setValues({});
      apply(res);
    });
  };

  // The person fixed the Excel: load the new copy into THIS import. Same layout keeps the
  // structure and mapping; a new layout is read again (by AI when it is on).
  const reload = (file) => {
    if (!file) return;
    run('reload', async () => {
      const res = await riskApi.reloadFile(batchId, file);
      setFields({});
      setValues({});
      apply(res);
      if (!res.reloaded?.same_layout && res.ai_available) {
        try {
          await riskApi.analyze(batchId, 'structure');
          apply(await riskApi.analyze(batchId, 'values'));
        } catch (_) {
          apply(await riskApi.importState(batchId));   // the page says why AI did not read it
        }
      }
    });
  };

  if (!st) {
    return (
      <div className="drv-page rsk-page">
        {error ? <div className="drv-error" role="alert">{error}</div> : <div className="drv-muted">Loading the import…</div>}
      </div>
    );
  }

  const isFile = st.batch.source === 'import';
  const targetsBy = Object.fromEntries((st.targets || []).map((t) => [t.name, t]));
  const usedTargets = new Set(Object.values(fields).filter((t) => t && targetsBy[t]));
  const nameMapped = Object.values(fields).includes('Obj_name');
  // A mandatory field no column is mapped to — the file may simply not have it (Assigned To), so
  // it can be given ONE value for every record instead of blocking the import.
  const unfilled = (st.targets || []).filter((t) => t.mandatory && !t.calculated && !usedTargets.has(t.name)
    && t.name !== 'Obj_name');
  const missing = (st.targets || []).filter((t) => t.mandatory && !t.calculated && !usedTargets.has(t.name)
    && !(t.name === 'Obj_name' && nameFrom.length) && !String(defaults[t.name] || '').trim());
  const curV = (st.values || []).find((v) => v.target === vtab);
  const shown = (v, x) => {
    const pick = (values[v.target] || {})[x.value];
    if (pick !== undefined) return pick;
    if (x.how === 'band') return bands.includes(v.target) ? (x.option || '') : '';
    return x.how === 'chosen' ? (x.option || '') : (x.option || '');
  };
  const openValues = (st.values || []).filter((v) => v.mandatory && v.values.some((x) => !shown(v, x) && x.how !== 'empty'));
  const colName = (i) => st.headers[i] || `column ${i + 1}`;
  const calculated = (st.targets || []).filter((t) => t.calculated);
  const methodProblems = st.problems?.method || [];
  const blocking = methodProblems.filter((p) => p.blocking);
  const savedFields = Object.fromEntries((st.mapping || []).map((m) => [m.col, m.target]));
  const unsaved = Object.entries(fields).some(([c, t]) => savedFields[c] !== t);
  const optText = (v, o) => {
    const code = (v.codes || {})[o];
    return code && /^\d+$/.test(code) && !String(o).trim().startsWith(code) ? `${code} — ${o}` : o;
  };

  return (
    <div className="drv-page rsk-page">
      <Crumbs panel={panel} last={isFile ? 'Import' : 'Review before create'}
        back={location.state?.from || (st.batch.source === 'studio' ? `/risk/${st.batch.setup_id}/studio` : '')} />
      {isFile && (
        <div className="rsk-steps-row">
          <Steps at={step} onPick={(n) => (n >= 2 ? setStep(n === 2 ? 3 : n) : null)} />
        </div>
      )}
      {error && <div className="drv-error" role="alert">{error}</div>}

      {isFile && step === 3 && (
        <>
          {/* ---- structure ---- */}
          <section className="rsk-card rsk-pad rsk-struct">
            <div className="rsk-struct-l">
              <div className="rsk-lbl">What we found in the file</div>
              <div className="rsk-row-between">
                <div className="rsk-struct-file">{st.batch.file_name} → sheet “{st.structure.sheet}”</div>
                <label className={`drv-btn rsk-reload${busy ? ' rsk-reload--off' : ''}`}
                  title="Load the file again — e.g. after fixing it in Excel. The same layout keeps your mapping.">
                  <input type="file" accept=".xlsx,.csv" disabled={!!busy}
                    onChange={(e) => { reload(e.target.files?.[0]); e.target.value = ''; }} />
                  {busy === 'reload' ? 'Reloading…' : 'Reload file'}
                </label>
              </div>
              {st.ai && (
                <div className="rsk-ai-note">
                  <strong>Read by AI</strong> from the first rows of each sheet — check it, then press Next.
                  {st.structure.notes && <div className="rsk-fine">{st.structure.notes}</div>}
                </div>
              )}
              {!st.ai && st.ai_error && (
                <div className="drv-note">AI could not read this file ({st.ai_error}). Below is the rule-based guess — check it, or try AI again.</div>
              )}
              <ul className="rsk-list">
                <li>Header on row{st.structure.header_rows.length > 1 ? 's' : ''} {st.structure.header_rows.map((h) => h + 1).join('–')}
                  {st.structure.header_rows.length > 1 && ' (group titles above column titles, joined)'}
                  {st.structure.data_start != null && ` · risks start on row ${st.structure.data_start + 1}`}</li>
                <li>{st.data_rows} rows → <strong>{st.record_count} records</strong>{st.structure.grain === 'group' ? ' (grouped)' : ', one per row'} · {st.skipped.length} skipped</li>
                {st.total_records > st.record_count && (
                  <li className="rsk-miss">The file has {st.total_records} records — only the first {st.max_records} are imported. Split the file to import the rest.</li>
                )}
                {st.structure.fill_down.length > 0 && <li>Merged cells copied down: {st.structure.fill_down.map(colName).join(', ')}</li>}
                {st.skipped.slice(0, 4).map((s) => <li key={s.row} className="rsk-fine">Row {s.row} skipped — {s.reason}: {s.text}</li>)}
              </ul>
              <div className="rsk-struct-ctl">
                <label className="drv-field"><span className="drv-lbl">Sheet</span>
                  <select className="drv-select" value={st.structure.sheet} disabled={!!busy}
                    onChange={(e) => setStructure({ sheet: e.target.value, header_rows: [0], fill_down: [] })}>
                    {st.sheets.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.rows} rows)</option>)}
                  </select>
                </label>
                <label className="drv-field"><span className="drv-lbl">Header row(s)</span>
                  <input className="drv-input" defaultValue={st.structure.header_rows.map((h) => h + 1).join(',')} key={st.structure.header_rows.join(',')}
                    onBlur={(e) => {
                      const hr = e.target.value.split(/[,\s–-]+/).map((x) => parseInt(x, 10) - 1).filter((x) => x >= 0);
                      if (hr.length && hr.join(',') !== st.structure.header_rows.join(',')) setStructure({ header_rows: hr });
                    }} />
                </label>
                <label className="drv-field"><span className="drv-lbl">One record per</span>
                  <select className="drv-select" value={st.structure.grain === 'group' ? String(st.structure.group_column) : 'row'} disabled={!!busy}
                    onChange={(e) => (e.target.value === 'row'
                      ? setStructure({ grain: 'row', group_column: null })
                      : setStructure({ grain: 'group', group_column: Number(e.target.value) }))}>
                    <option value="row">row</option>
                    {st.headers.map((h, i) => <option key={h} value={String(i)}>value of “{h}”</option>)}
                  </select>
                </label>
              </div>
              <details className="rsk-filldown">
                <summary className="rsk-fine">Merged cells — copy a value down into the blank rows under it</summary>
                <div className="rsk-fine">Tick a column when one cell is merged over several risks (e.g. one process step for five
                  risks), so every risk gets the value — not only the first.</div>
                {st.headers.map((h, i) => (
                  <label key={h} className="drv-chk">
                    <input type="checkbox" checked={st.structure.fill_down.includes(i)} disabled={!!busy}
                      onChange={(e) => setStructure({ fill_down: e.target.checked ? [...st.structure.fill_down, i] : st.structure.fill_down.filter((x) => x !== i) })} />
                    <span>{h}</span>
                  </label>
                ))}
              </details>
            </div>
            <div className="rsk-struct-r">
              <div className="rsk-lbl">Preview — from row {st.preview_first_row}</div>
              <div className="rsk-table-wrap">
                <table className="rsk-xl">
                  <tbody>
                    {st.preview.map((r, i) => (
                      <tr key={i} className={st.structure.header_rows.includes(st.preview_first_row - 1 + i) ? 'hdr' : ''}>
                        <td className="rn">{st.preview_first_row + i}</td>
                        {r.map((c, j) => <td key={j}>{c}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ---- scoring method: the project's FMEA settings vs the file ---- */}
          {st.method && (
            <section className={`rsk-card rsk-pad rsk-method${blocking.length ? ' rsk-method--stop' : ''}`}>
              <div className="rsk-row-between">
                <div>
                  <div className="rsk-lbl">How this project scores a risk</div>
                  <div className="rsk-method-formula">{st.method.formula}</div>
                  <div className="rsk-fine">
                    Scores in: {st.method.inputs.join(' · ')} · scale 1–{st.method.range}
                    {st.method.same_severity ? ' · severity stays the same after control' : ''}
                    {st.method.source === 'default' ? ' · default — the project’s FMEA settings could not be read' : ' · from the project’s FMEA settings in Orcanos'}
                  </div>
                </div>
                {!blocking.length && st.method.source !== 'default' && <span className="rsk-pill" style={CONF_STYLE.exact}>The file fits</span>}
              </div>
              {methodProblems.map((p) => (
                <div key={`${p.code}-${p.phase}`} className={p.blocking ? 'drv-error rsk-method-prob' : 'drv-note rsk-method-prob'} role={p.blocking ? 'alert' : undefined}>
                  <strong>{p.blocking ? 'Stop — ' : ''}{p.title}.</strong> {p.detail}
                  {p.cols?.length > 0 && (
                    <div className="rsk-actions" style={{ marginTop: 8 }}>
                      <button type="button" className="drv-btn" disabled={!!busy} onClick={() => keepAsText(p.cols)}>
                        Keep {p.cols.map((c) => `“${colName(c)}”`).join(', ')} as text in the description
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {calculated.length > 0 && (
                <div className="rsk-method-calc">
                  <span className="rsk-lbl">Automatic calculation — not imported</span>
                  {calculated.map((t) => <span key={t.name} className="rsk-chip rsk-chip--calc" title={t.calculated}>{t.title}</span>)}
                  <span className="rsk-fine">Orcanos calculates these from the scores, so a column in the file is never copied into them.</span>
                </div>
              )}
            </section>
          )}

          {/* ---- field mapping ---- */}
          <section className="rsk-card">
            <div className="rsk-pad rsk-row-between" style={{ borderBottom: '1px solid #EFECF6' }}>
              <div>
                <h2 className="rsk-card-title">Map columns to Risk fields</h2>
                <div className="rsk-card-meta">
                  Fields come from the Risk form in Orcanos{st.template ? ' · a saved template for this layout was applied' : ''}.
                  {missing.length > 0 && <span className="rsk-miss"> Mandatory and unmapped: {missing.map((t) => t.title).join(', ')}.</span>}
                </div>
              </div>
              <div className="rsk-actions">
                {st.ai_available && (
                  <button type="button" className="drv-btn" onClick={askAi} disabled={!!busy}>
                    {busy === 'ai' ? 'AI is reading the file…' : (st.ai ? 'Read again with AI' : 'Map with AI')}
                  </button>
                )}
              </div>
            </div>
            <div className="rsk-table-wrap">
              <table className="rsk-table" style={{ minWidth: 980 }}>
                <thead><tr><th>Excel column</th><th>Sample values</th><th>→ Risk field</th><th>Confidence</th><th>Why</th></tr></thead>
                <tbody>
                  {st.mapping.map((m) => {
                    const t = fields[m.col] ?? m.target;
                    const changed = t !== m.target;
                    const conf = changed ? (t ? 'chosen' : 'unmapped') : m.confidence;
                    const cs = CONF_STYLE[conf] || CONF_STYLE.unmapped;
                    return (
                      <tr key={m.col} style={!t ? { opacity: 0.7 } : undefined}>
                        <td style={{ fontWeight: 600 }}>{m.header}</td>
                        <td>{(m.samples || []).map((s) => <span key={s} className="rsk-chip">{s}</span>)}</td>
                        <td>
                          <select className="drv-select rsk-map-sel" aria-label={`Field for column ${m.header}`} value={t}
                            onChange={(e) => setFields((f) => ({ ...f, [m.col]: e.target.value }))}>
                            <option value="">— ignore —</option>
                            {Object.entries(st.pseudo_targets).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                            <optgroup label="Risk fields">
                              {st.targets.map((x) => (
                                <option key={x.name} value={x.name} disabled={!!x.calculated || (usedTargets.has(x.name) && t !== x.name)}
                                  title={x.calculated || undefined}>
                                  {x.title}{x.mandatory ? ' *' : ''}{x.calculated ? ' — automatic calculation' : ''}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </td>
                        <td><span className="rsk-pill" style={cs}>{cs.label}</span></td>
                        <td className="rsk-sub">{changed ? (t ? 'Chosen by you' : '') : m.why}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!nameMapped && (
              <div className="rsk-pad rsk-namefrom">
                <span className="rsk-lbl">Name is built from</span>
                {st.headers.map((h, i) => (
                  <label key={h} className="drv-chk">
                    <input type="checkbox" checked={nameFrom.includes(i)}
                      onChange={(e) => setNameFrom((n) => (e.target.checked ? [...n, i] : n.filter((x) => x !== i)))} />
                    <span>{h}</span>
                  </label>
                ))}
                <span className="rsk-fine">joined with “ — ”, because no column is the risk’s name.</span>
              </div>
            )}
            {unfilled.length > 0 && (
              <div className="rsk-pad rsk-namefrom">
                <span className="rsk-lbl">Not in the file — same value for every record</span>
                {unfilled.map((t) => (
                  <label key={t.name} className="rsk-default">
                    <span>{t.title} *</span>
                    {t.options?.length && t.name !== 'Assigned_to' ? (
                      <select className="drv-select" aria-label={`Value of ${t.title} for every record`}
                        value={defaults[t.name] || ''} onChange={(e) => setDefaults((d) => ({ ...d, [t.name]: e.target.value }))}>
                        <option value="">— choose —</option>
                        {t.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className="drv-input" aria-label={`Value of ${t.title} for every record`}
                        placeholder={t.name === 'Assigned_to' ? 'Orcanos user name' : ''}
                        value={defaults[t.name] || ''} onChange={(e) => setDefaults((d) => ({ ...d, [t.name]: e.target.value }))} />
                    )}
                  </label>
                ))}
                <span className="rsk-fine">No column is mapped to {unfilled.length > 1 ? 'these mandatory fields' : 'this mandatory field'}. Assigned To starts as you.</span>
              </div>
            )}
          </section>

          {/* ---- value mapping ---- */}
          {(st.values || []).length > 0 && (
            <section className="rsk-card">
              <div className="rsk-tabs" role="tablist">
                {st.values.map((v) => (
                  <button key={v.target} type="button" role="tab" aria-selected={vtab === v.target}
                    className={vtab === v.target ? 'on' : ''} onClick={() => setVtab(v.target)}>
                    {v.title}{v.needs_you ? ` · ${v.needs_you} need you` : ' · matched'}
                  </button>
                ))}
              </div>
              {curV && (
                <div className="rsk-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {curV.band && (
                    <div className="drv-note">
                      <strong>Scale mismatch.</strong> “{curV.header}” scores 1–{curV.band.file_max}; Orcanos uses 1–{curV.band.option_max}.
                      Proposed: {curV.band.ranges.map((r) => `${r.from}–${r.to} → ${r.option}`).join(' · ')}.
                      Values are never divided silently.{' '}
                      {bands.includes(curV.target)
                        ? <button type="button" className="drv-link" onClick={() => setBands((b) => b.filter((x) => x !== curV.target))}>Confirmed — undo</button>
                        : <button type="button" className="drv-btn drv-btn--primary" onClick={() => setBands((b) => [...b, curV.target])}>Confirm this banding</button>}
                    </div>
                  )}
                  <table className="rsk-table" style={{ minWidth: 560 }}>
                    <thead><tr><th>Value in file</th><th>Rows</th><th>→ Orcanos value</th><th>How</th></tr></thead>
                    <tbody>
                      {curV.values.map((x) => {
                        const chosen = shown(curV, x);
                        const picked = (values[curV.target] || {})[x.value] !== undefined;
                        const how = picked ? (chosen ? 'chosen' : 'none')
                          : x.how === 'band' ? (bands.includes(curV.target) ? 'number' : 'band')
                            : (chosen ? x.how : (x.how === 'empty' ? 'empty' : 'none'));
                        const hs = HOW_STYLE[how] || HOW_STYLE.none;
                        return (
                          <tr key={x.value}>
                            <td style={{ fontWeight: 600 }}>{x.value}</td>
                            <td>{x.count}</td>
                            <td>
                              <select className="drv-select rsk-map-sel" aria-label={`Map ${x.value}`} value={chosen}
                                onChange={(e) => setValues((vv) => ({ ...vv, [curV.target]: { ...(vv[curV.target] || {}), [x.value]: e.target.value } }))}>
                                <option value="">— leave empty —</option>
                                {curV.options.map((o) => <option key={o} value={o}>{optText(curV, o)}</option>)}
                              </select>
                            </td>
                            <td><span className="rsk-pill" style={{ background: hs[1], color: hs[2] }}>{hs[0]}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          <div className="rsk-row-between">
            <label className="drv-chk"><input type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} />
              <span>Save this mapping as a template for files with the same layout</span></label>
            <div className="rsk-actions">
              {blocking.length > 0 && <span className="rsk-miss">{unsaved ? 'Save to check the scoring again' : 'The file scores risks differently — see above'}</span>}
              {missing.length > 0 && <span className="rsk-miss">Map {missing.map((t) => t.title).join(', ')} to continue</span>}
              {openValues.length > 0 && missing.length === 0 && <span className="rsk-miss">Map the values of {openValues.map((v) => v.title).join(', ')}</span>}
              <button type="button" className="drv-btn drv-btn--lg" onClick={saveMapping} disabled={!!busy}>Save</button>
              <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={toReview}
                disabled={!!busy || missing.length > 0 || openValues.length > 0 || (blocking.length > 0 && !unsaved)}>
                {busy === 'stage' ? 'Checking every record…' : 'Next: review records'}
              </button>
            </div>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          {isFile && (
            <div className="rsk-row-between">
              <div className="drv-sub" style={{ margin: 0 }}>{st.batch.file_name} · {st.record_count} records proposed</div>
              <button type="button" className="drv-btn" onClick={() => setStep(3)}>Back to mapping</button>
            </div>
          )}
          <StagingReview batchId={batchId} />
        </>
      )}
    </div>
  );
}

// Each step owns a slice of the bar. Upload fills its slice by real bytes; an AI step's length
// is unknown, so its slice fills toward (never to) its end on a typical duration, and jumps to
// the end only when the request returns.
const UPLOAD_STEPS = [
  { label: 'Uploading the file', share: 0.15 },
  { label: 'AI is reading the layout and mapping the columns', share: 0.7, typical: 20 },
  { label: 'AI is matching values to the Risk form', share: 0.15, typical: 5 },
];

function UploadProgress({ prog }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 250);
    return () => clearInterval(t);
  }, []);
  const steps = prog.steps;
  const done = steps.slice(0, prog.step).reduce((a, s) => a + s.share, 0);
  const cur = steps[prog.step];
  let part = 0;
  if (cur) {
    const secs = (Date.now() - prog.since) / 1000;
    part = cur.typical ? 0.95 * (1 - Math.exp(-secs / (cur.typical / 2))) : prog.pct;
  }
  const pct = Math.min(100, Math.round((done + (cur ? cur.share * part : 0)) * 100));
  const secs = Math.round((Date.now() - prog.since) / 1000);
  return (
    <section className="rsk-card rsk-pad rsk-upprog" aria-live="polite">
      <div className="rsk-row-between">
        <strong>{cur ? cur.label : 'Opening the import'}…</strong>
        <span className="rsk-fine">{pct}%</span>
      </div>
      <div className="drv-bar drv-bar--wide" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <i style={{ width: `${pct}%` }} />
      </div>
      <ol className="rsk-upprog-steps">
        {steps.map((s, i) => (
          <li key={s.label} className={i < prog.step ? 'done' : (i === prog.step ? 'on' : '')}>
            <b>{i < prog.step ? '✓' : i + 1}</b>{s.label}
            {i === prog.step && s.typical && <span className="rsk-fine"> · {secs}s (usually about {s.typical}s)</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}

function Crumbs({ panel, last, back }) {
  // The Orcanos project the records will be created in — the panel stores only its id.
  const [project, setProject] = useState('');
  const pid = panel?.project_id;
  useEffect(() => {
    if (!pid) return undefined;
    let live = true;
    riskApi.setup().then((d) => {
      const p = (d.projects || []).find((x) => String(x.project_id) === String(pid));
      if (live) setProject(p?.project_name || `Project ${pid}`);
    }).catch(() => { if (live) setProject(`Project ${pid}`); });
    return () => { live = false; };
  }, [pid]);
  return (
    <div className="rsk-crumbs">
      {back && <Link className="drv-btn rsk-back" to={back}>← Back to Risk Studio</Link>}
      <Link to="/risk">Risk</Link><span>/</span>
      <span className="rsk-dim">{panel?.name || 'Risk file'}</span><span>/</span>
      <strong>{last}</strong>
      {project && (
        <span className="rsk-crumb-proj" title="The Orcanos project the records are created in">
          Orcanos project: <strong>{project}</strong>
        </span>
      )}
    </div>
  );
}

function Steps({ at, onPick }) {
  const steps = ['Upload', 'Structure', 'Map fields & values', 'Review records'];
  return (
    <div className="rsk-steps">
      {steps.map((s, i) => {
        const n = i + 1;
        const done = n < at || (n === 2 && at >= 3);
        const cur = n === at || (n === 2 && at === 3);
        return (
          <React.Fragment key={s}>
            {i > 0 && <span className="rsk-step-sep" aria-hidden="true">—</span>}
            <button type="button" className={`rsk-step${cur ? ' rsk-step--on' : ''}${done ? ' rsk-step--done' : ''}`}
              onClick={onPick ? () => onPick(n) : undefined} disabled={!onPick || n === 1}>
              <b>{done && !cur ? '✓' : n}</b>{s}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}
